import { useCallback, useEffect, useRef, useState } from 'react';
import { request } from './api.js';

/**
 * Nav badge counts — small "needs your attention" hints rendered as red dots
 * on the top-bar tabs so the admin doesn't have to remember to click in.
 *
 *   feedback — untreated user-feedback items (server `unread_total`)
 *   releases — 1 when a renderer draft is waiting for approval, else 0
 *   orders   — paid orders that arrived since this tab was last viewed
 *   users    — signups that arrived since this tab was last viewed
 *
 * Two badge kinds:
 *   • server-state (feedback, releases) — the dot reflects a live backend
 *     condition and clears when the admin acts on it (the condition flips).
 *   • new-since-seen (orders, users) — there is no "unread" flag, so we keep a
 *     per-tab "last seen count" in localStorage and badge the positive delta.
 *     Visiting the tab acknowledges it (baseline ← current → delta 0). The
 *     baseline also follows the count *down* (e.g. orders_paid_30d rolls off
 *     the 30-day window, or a user is deleted) so aging never masks a genuine
 *     new arrival.
 *
 * Fetches are best-effort and decorative: any failure (e.g. a transient 401
 * mid token-refresh) degrades to 0 instead of surfacing an error, because a
 * missing dot must never break navigation. We refresh on mount, on every route
 * change (so a dot clears right after the admin acts), on a slow poll, and when
 * the tab regains focus.
 */
export interface NavBadges {
  feedback: number;
  releases: number;
  orders: number;
  users: number;
}

interface StatsShape {
  total_users: number;
  orders_paid_30d: number;
}

const EMPTY: NavBadges = { feedback: 0, releases: 0, orders: 0, users: 0 };
const POLL_MS = 60_000;
const SEEN_KEY = 'little-shark-admin-nav-seen';

function readSeen(): Record<string, number> {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, number>) : {};
  } catch {
    // Corrupt JSON / localStorage unavailable (private mode) — start fresh.
    return {};
  }
}

function writeSeen(seen: Record<string, number>): void {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(seen));
  } catch {
    // localStorage unavailable (private mode) — last-seen just won't persist.
  }
}

/**
 * Delta for a "new since last seen" tab. Mutates `seen[key]` as a side effect:
 * acknowledges (when viewing the tab), seeds the baseline on first sight, and
 * ratchets the baseline down when the count shrinks. Returns the count to badge.
 */
function newSinceSeen(
  seen: Record<string, number>,
  key: string,
  current: number | undefined,
  acknowledge: boolean,
): number {
  if (current === undefined) return 0;
  const prev = seen[key];
  if (acknowledge || prev === undefined || current < prev) {
    seen[key] = current;
    return 0;
  }
  return current - prev;
}

export function useNavBadges(pathname: string): NavBadges {
  const [badges, setBadges] = useState<NavBadges>(EMPTY);
  // Keep the latest pathname readable from the interval/focus handlers without
  // tearing down the timer on every navigation.
  const pathRef = useRef(pathname);
  pathRef.current = pathname;

  const refresh = useCallback(async (): Promise<void> => {
    const currentPath = pathRef.current;
    const [feedback, releases, stats] = await Promise.all([
      // limit=1 keeps the payload tiny — we only read the global unread_total.
      request<{ unread_total: number }>('/admin/feedback?limit=1&offset=0')
        .then((r) => r.unread_total ?? 0)
        .catch(() => 0),
      request<{ draft: unknown }>('/admin/releases/renderer')
        .then((r) => (r.draft != null ? 1 : 0))
        .catch(() => 0),
      request<{ stats: StatsShape }>('/admin/stats')
        .then((r) => r.stats)
        .catch(() => null),
    ]);

    const seen = readSeen();
    const orders = newSinceSeen(seen, 'orders', stats?.orders_paid_30d, currentPath === '/orders');
    const users = newSinceSeen(seen, 'users', stats?.total_users, currentPath === '/users');
    writeSeen(seen);

    setBadges({ feedback, releases, orders, users });
  }, []);

  // Mount + every route change: dots refresh right after the admin acts on an
  // item, without waiting for the slow poll.
  useEffect(() => {
    void refresh();
  }, [refresh, pathname]);

  // Slow background poll + refetch when the tab regains focus.
  useEffect(() => {
    const id = window.setInterval(() => void refresh(), POLL_MS);
    const onFocus = (): void => void refresh();
    window.addEventListener('focus', onFocus);
    return (): void => {
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh]);

  return badges;
}
